import type { PropCategory, PropDefinition } from '@/site/PropCatalog';
import { safeColor } from './FloraThumbnail';

/**
 * Palette thumbnails for objects, drawn as inline SVG from the record's measured size and colours.
 *
 * The same bargain the flora and fauna thumbnails strike — a vector silhouette rather than an
 * offscreen render per tile — with one extra reason that only applies here: a prop's mesh may not
 * have downloaded yet, and a palette whose tiles fill in one by one over a slow connection is worse
 * than one that never claimed to show the asset. These are drawn from the catalog record, which is
 * always present, so the palette is complete the moment it opens.
 *
 * Proportions are real. The record carries the geometry's measured width, height and depth, so a
 * bench reads as long and low and a lamp post as tall and thin without anyone drawing either.
 */

const VIEW = { width: 64, height: 48 } as const;
const GROUND = 42;

/**
 * Screen height for a real height in metres, over the 0.2 m – 4 m range a garden object occupies.
 *
 * Linear rather than the log scale fauna needs: that range is only twenty-fold and every object in
 * it is comfortably visible, so the honest mapping is the plain one.
 */
function scale(prop: PropDefinition): { w: number; h: number } {
  const perMeter = 15;
  // The floor is 11px, not the 5px a first pass used: a 0.63 m rain barrel came out seven pixels
  // wide and could not be told from a bollard. Objects are identified in this palette rather than
  // compared, so legibility wins the tie that fauna resolves the other way.
  const h = Math.max(11, Math.min(34, prop.size.heightMeters * perMeter));
  const w = Math.max(11, Math.min(52, prop.size.widthMeters * perMeter));
  return { w, h };
}

const round = (n: number): string => n.toFixed(2);

export function objectThumbnailSvg(prop: PropDefinition): string {
  const body = safeColor(prop.colors.body, '#8a7f6d');
  const trim = safeColor(prop.colors.trim, '#4b4b4b');
  const { w, h } = scale(prop);
  const cx = VIEW.width / 2;
  const x = cx - w / 2;
  const y = GROUND - h;

  return [
    `<svg viewBox="0 0 ${VIEW.width} ${VIEW.height}" width="100%" height="100%" role="img" aria-hidden="true">`,
    `<line x1="4" y1="${GROUND}.5" x2="${VIEW.width - 4}" y2="${GROUND}.5" stroke="currentColor" stroke-opacity="0.22" stroke-width="1"/>`,
    shapeFor(prop.category, { x, y, w, h, cx, body, trim }),
    '</svg>',
  ].join('');
}

interface ShapeArgs {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  body: string;
  trim: string;
}

/**
 * A per-category silhouette.
 *
 * Category rather than per-prop art, because the category is what the shape actually encodes: every
 * seat has a back and a seat plane, every light has a head on a stem, and a rock is a rock. Twelve
 * shapes cover a catalog that could grow to hundreds without anyone drawing another one.
 */
function shapeFor(category: PropCategory, a: ShapeArgs): string {
  switch (category) {
    case 'seating':
      return [
        `<rect x="${round(a.x)}" y="${round(a.y + a.h * 0.45)}" width="${round(a.w)}" height="${round(a.h * 0.16)}" fill="${a.body}"/>`,
        `<rect x="${round(a.x)}" y="${round(a.y)}" width="${round(a.w * 0.14)}" height="${round(a.h * 0.5)}" fill="${a.body}"/>`,
        `<rect x="${round(a.x + a.w * 0.08)}" y="${round(a.y + a.h * 0.6)}" width="${round(a.w * 0.08)}" height="${round(a.h * 0.4)}" fill="${a.trim}"/>`,
        `<rect x="${round(a.x + a.w * 0.84)}" y="${round(a.y + a.h * 0.6)}" width="${round(a.w * 0.08)}" height="${round(a.h * 0.4)}" fill="${a.trim}"/>`,
      ].join('');
    case 'lighting':
      return [
        `<rect x="${round(a.cx - a.w * 0.07)}" y="${round(a.y + a.h * 0.2)}" width="${round(a.w * 0.14)}" height="${round(a.h * 0.8)}" fill="${a.trim}"/>`,
        `<path d="M ${round(a.cx - a.w * 0.3)} ${round(a.y + a.h * 0.24)} L ${round(a.cx)} ${round(a.y)} L ${round(a.cx + a.w * 0.3)} ${round(a.y + a.h * 0.24)} Z" fill="${a.body}"/>`,
        `<rect x="${round(a.cx - a.w * 0.22)}" y="${round(GROUND - a.h * 0.05)}" width="${round(a.w * 0.44)}" height="${round(a.h * 0.05)}" fill="${a.trim}"/>`,
      ].join('');
    case 'container':
      return [
        `<path d="M ${round(a.x + a.w * 0.14)} ${round(a.y)} L ${round(a.x + a.w * 0.86)} ${round(a.y)} L ${round(a.x + a.w * 0.72)} ${round(a.y + a.h)} L ${round(a.x + a.w * 0.28)} ${round(a.y + a.h)} Z" fill="${a.body}"/>`,
        `<rect x="${round(a.x + a.w * 0.1)}" y="${round(a.y)}" width="${round(a.w * 0.8)}" height="${round(a.h * 0.14)}" fill="${a.trim}"/>`,
      ].join('');
    case 'rock':
      return `<path d="M ${round(a.x)} ${round(GROUND)} L ${round(a.x + a.w * 0.18)} ${round(a.y + a.h * 0.25)} L ${round(a.x + a.w * 0.5)} ${round(a.y)} L ${round(a.x + a.w * 0.82)} ${round(a.y + a.h * 0.35)} L ${round(a.x + a.w)} ${round(GROUND)} Z" fill="${a.body}"/>`;
    case 'water':
      return [
        `<ellipse cx="${round(a.cx)}" cy="${round(GROUND - a.h * 0.12)}" rx="${round(a.w / 2)}" ry="${round(a.h * 0.16)}" fill="${a.body}"/>`,
        `<ellipse cx="${round(a.cx)}" cy="${round(a.y + a.h * 0.2)}" rx="${round(a.w * 0.36)}" ry="${round(a.h * 0.14)}" fill="${a.trim}"/>`,
        `<rect x="${round(a.cx - a.w * 0.06)}" y="${round(a.y + a.h * 0.2)}" width="${round(a.w * 0.12)}" height="${round(a.h * 0.7)}" fill="${a.trim}"/>`,
      ].join('');
    case 'structure':
      return [
        `<rect x="${round(a.x)}" y="${round(a.y + a.h * 0.35)}" width="${round(a.w)}" height="${round(a.h * 0.65)}" fill="${a.body}"/>`,
        `<path d="M ${round(a.x - a.w * 0.06)} ${round(a.y + a.h * 0.35)} L ${round(a.cx)} ${round(a.y)} L ${round(a.x + a.w * 1.06)} ${round(a.y + a.h * 0.35)} Z" fill="${a.trim}"/>`,
      ].join('');
    case 'play':
      return [
        `<path d="M ${round(a.x)} ${round(GROUND)} L ${round(a.cx)} ${round(a.y)} L ${round(a.x + a.w)} ${round(GROUND)}" stroke="${a.trim}" stroke-width="2" fill="none"/>`,
        `<rect x="${round(a.cx - a.w * 0.16)}" y="${round(a.y + a.h * 0.62)}" width="${round(a.w * 0.32)}" height="${round(a.h * 0.1)}" fill="${a.body}"/>`,
      ].join('');
    case 'art':
      return [
        `<rect x="${round(a.cx - a.w * 0.3)}" y="${round(GROUND - a.h * 0.16)}" width="${round(a.w * 0.6)}" height="${round(a.h * 0.16)}" fill="${a.trim}"/>`,
        `<path d="M ${round(a.cx - a.w * 0.22)} ${round(GROUND - a.h * 0.16)} L ${round(a.cx)} ${round(a.y)} L ${round(a.cx + a.w * 0.22)} ${round(GROUND - a.h * 0.16)} Z" fill="${a.body}"/>`,
      ].join('');
    case 'edging':
      return `<rect x="${round(a.x)}" y="${round(GROUND - Math.max(3, a.h * 0.4))}" width="${round(a.w)}" height="${round(Math.max(3, a.h * 0.4))}" fill="${a.body}"/>`;
    case 'vehicle':
      return [
        `<rect x="${round(a.x)}" y="${round(a.y + a.h * 0.3)}" width="${round(a.w)}" height="${round(a.h * 0.45)}" rx="${round(a.h * 0.12)}" fill="${a.body}"/>`,
        `<circle cx="${round(a.x + a.w * 0.22)}" cy="${round(GROUND - a.h * 0.12)}" r="${round(a.h * 0.14)}" fill="${a.trim}"/>`,
        `<circle cx="${round(a.x + a.w * 0.78)}" cy="${round(GROUND - a.h * 0.12)}" r="${round(a.h * 0.14)}" fill="${a.trim}"/>`,
      ].join('');
    case 'utility':
      return [
        `<rect x="${round(a.x + a.w * 0.1)}" y="${round(a.y + a.h * 0.12)}" width="${round(a.w * 0.8)}" height="${round(a.h * 0.88)}" rx="${round(a.w * 0.08)}" fill="${a.body}"/>`,
        `<rect x="${round(a.x)}" y="${round(a.y)}" width="${round(a.w)}" height="${round(a.h * 0.14)}" fill="${a.trim}"/>`,
      ].join('');
    case 'feature':
    default:
      return [
        `<ellipse cx="${round(a.cx)}" cy="${round(GROUND - a.h * 0.3)}" rx="${round(a.w / 2)}" ry="${round(a.h * 0.42)}" fill="${a.body}"/>`,
        `<ellipse cx="${round(a.cx)}" cy="${round(GROUND - a.h * 0.55)}" rx="${round(a.w * 0.28)}" ry="${round(a.h * 0.18)}" fill="${a.trim}"/>`,
      ].join('');
  }
}
